/**
 * CREATIVE DETONATOR - XSS Prevention Utilities
 * Centralized sanitization for all user/API data before DOM insertion
 *
 * @version 1.0.0
 * @description Single source of truth for HTML sanitization.
 * Prevents XSS attacks by escaping special characters before innerHTML/insertAdjacentHTML usage.
 */

// ============================================================================
// HTML ENTITY ENCODING
// ============================================================================

/**
 * Escape HTML special characters to prevent XSS
 * @param {string|any} str - Value to escape
 * @returns {string} Escaped string safe for innerHTML
 *
 * @example
 * escapeHtml('<script>alert("XSS")</script>')
 * // Returns: '&lt;script&gt;alert(&quot;XSS&quot;)&lt;/script&gt;'
 */
export function escapeHtml(str) {
  if (str === null || str === undefined) return '';
  if (typeof str !== 'string') str = String(str);

  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
    .replace(/`/g, '&#x60;');
}

/**
 * Escape for use in HTML attributes
 * Also handles newlines for multiline attributes
 * @param {string|any} str - Value to escape
 * @returns {string} Escaped string safe for HTML attributes
 */
export function escapeAttr(str) {
  if (str === null || str === undefined) return '';
  return escapeHtml(str)
    .replace(/\n/g, '&#10;')
    .replace(/\r/g, '&#13;');
}

// ============================================================================
// TEMPLATE TAG FOR SAFE HTML
// ============================================================================

/**
 * Marker object for pre-escaped content
 * @private
 */
class SafeHtmlMarker {
  constructor(content) {
    this.__safeHtml = true;
    this.content = content ?? '';
  }
}

/**
 * Tagged template literal for safe HTML construction
 * Automatically escapes all interpolated values
 *
 * @param {TemplateStringsArray} strings - Template literal strings
 * @param {...any} values - Interpolated values
 * @returns {string} Safe HTML string
 *
 * @example
 * // Automatic escaping
 * const userInput = '<script>alert("XSS")</script>';
 * html`<div>${userInput}</div>`
 * // Returns: '<div>&lt;script&gt;alert(&quot;XSS&quot;)&lt;/script&gt;</div>'
 *
 * @example
 * // Pre-escaped content (trusted)
 * const trustedHtml = '<strong>Bold</strong>';
 * html`<div>${safeHtml(trustedHtml)}</div>`
 * // Returns: '<div><strong>Bold</strong></div>'
 *
 * @example
 * // Arrays (e.g., from .map())
 * const items = ['<script>1</script>', 'Item 2'];
 * html`<ul>${items.map(i => html`<li>${i}</li>`)}</ul>`
 * // Returns safe HTML with escaped items
 */
export function html(strings, ...values) {
  let result = strings[0];

  for (let i = 0; i < values.length; i++) {
    const value = values[i];

    // Handle SafeHtml marker objects
    if (value && value.__safeHtml === true) {
      result += value.content;
    }
    // Handle arrays (map results)
    else if (Array.isArray(value)) {
      result += value.map(v =>
        v && v.__safeHtml === true ? v.content : escapeHtml(v)
      ).join('');
    }
    // Escape regular values
    else {
      result += escapeHtml(value);
    }

    result += strings[i + 1];
  }

  return result;
}

/**
 * Mark content as pre-escaped/trusted (bypass escaping)
 * USE SPARINGLY - only for content that is already safe
 *
 * @param {string} content - Pre-escaped HTML content
 * @returns {SafeHtmlMarker} Marker object that html`` won't escape
 *
 * @example
 * // For content you've already sanitized or generated safely
 * const icon = '<svg>...</svg>';
 * html`<button>${safeHtml(icon)} Click me</button>`
 */
export function safeHtml(content) {
  return new SafeHtmlMarker(content);
}

// ============================================================================
// URL VALIDATION
// ============================================================================

/**
 * Check if URL is safe (no javascript:, data:, etc.)
 * @param {string} url - URL to validate
 * @returns {boolean} True if URL is safe
 */
export function isUrlSafe(url) {
  if (!url || typeof url !== 'string') return false;

  const normalized = url.toLowerCase().trim();
  const dangerous = ['javascript:', 'data:', 'vbscript:', 'file:'];

  for (const proto of dangerous) {
    if (normalized.startsWith(proto)) return false;
  }

  return true;
}

/**
 * Sanitize URL for href attribute
 * Returns safe URL or '#' for dangerous URLs
 *
 * @param {string} url - URL to sanitize
 * @returns {string} Safe URL or '#'
 */
export function sanitizeUrl(url) {
  return isUrlSafe(url) ? url : '#';
}

/**
 * Escape URL for use in HTML
 * Encodes special characters that could break out of attributes
 *
 * @param {string} url - URL to escape
 * @returns {string} Escaped URL safe for href/src attributes
 */
export function escapeUrl(url) {
  if (!url || typeof url !== 'string') return '';
  // First validate, then escape for attribute context
  return isUrlSafe(url) ? escapeAttr(url) : '#';
}

// ============================================================================
// CSS VALUE SANITIZATION
// ============================================================================

/**
 * Sanitize CSS value to prevent CSS injection
 * @param {string} value - CSS value to sanitize
 * @returns {string} Safe CSS value
 */
export function sanitizeCss(value) {
  if (!value || typeof value !== 'string') return '';

  // Remove potentially dangerous patterns
  return value
    .replace(/expression\s*\(/gi, '')
    .replace(/url\s*\(/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/[;{}]/g, '');
}

/**
 * Validate and return a safe color value
 * @param {string} color - Color value (hex, rgb, etc.)
 * @returns {string} Safe color value or fallback
 */
export function sanitizeColor(color, fallback = '#000000') {
  if (!color || typeof color !== 'string') return fallback;

  // Allow hex colors
  if (/^#[0-9A-Fa-f]{3,8}$/.test(color)) return color;

  // Allow rgb/rgba
  if (/^rgba?\s*\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/.test(color)) return color;

  // Allow named colors (basic list)
  const namedColors = ['black', 'white', 'red', 'green', 'blue', 'yellow', 'cyan', 'magenta', 'transparent'];
  if (namedColors.includes(color.toLowerCase())) return color;

  return fallback;
}

// ============================================================================
// GLOBAL EXPORT (for non-module usage)
// ============================================================================

window.Sanitize = {
  escapeHtml,
  escapeAttr,
  html,
  safeHtml,
  isUrlSafe,
  sanitizeUrl,
  escapeUrl,
  sanitizeCss,
  sanitizeColor
};

export default {
  escapeHtml,
  escapeAttr,
  html,
  safeHtml,
  isUrlSafe,
  sanitizeUrl,
  escapeUrl,
  sanitizeCss,
  sanitizeColor
};
